import { useMutation } from "@apollo/client/react"
import React, { useCallback, useRef, useState } from "react"
import { toast } from "sonner"
import { gql } from "~/__generated__"
import { useViewer } from "~/auth/use-viewer"
import { directImageUpload } from "~/common/direct-image-upload"
import { Button } from "~/ui/button"
import { UserAvatar } from "~/users/user-avatar"

const viewerUpdateAvatarMutation = gql(/* GraphQL */ `
  mutation ViewerUpdate($input: ViewerUpdateInput!) {
    viewerUpdate(input: $input) {
      user {
        id
        avatarThumbUrl
      }
    }
  }
`)

export const AvatarUpload = () => {
  const [isSaving, setIsSaving] = useState(false)
  const fileInputRef = useRef<HTMLInputElement | null>(null)

  const { viewer } = useViewer()

  const [runViewerUpdate] = useMutation(viewerUpdateAvatarMutation)

  const onFileSelect = useCallback(
    async (file: File) => {
      setIsSaving(true)

      const { signedId } = await directImageUpload(file)

      const { error } = await runViewerUpdate({
        variables: {
          input: {
            viewerInput: {},
            avatarSignedId: signedId,
          },
        },
      })

      if (error) {
        toast.error("Failed to save avatar")
      }

      setIsSaving(false)
    },
    [runViewerUpdate]
  )

  const removeAvatar = async (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()

    try {
      const { error } = await runViewerUpdate({
        variables: {
          input: {
            viewerInput: {},
            removeAvatar: true,
          },
        },
      })

      if (error) {
        toast.error("Failed to remove avatar")
      }
    } catch {
      toast.error("Error removing avatar")
    }
  }

  const onClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()

    if (fileInputRef.current) {
      fileInputRef.current.click()
    }
  }

  const cta = viewer.avatarThumbUrl ? "Change Photo" : "Choose Photo"

  return (
    <>
      <input
        type="file"
        ref={fileInputRef}
        className="hidden"
        accept="image/jpeg, image/png"
        onChange={(event) => {
          if (event.target.files && event.target.files.length > 0) {
            onFileSelect(event.target.files[0])
          }
        }}
      />
      <div className="flex items-center gap-5">
        <UserAvatar user={viewer} className="h-24 w-24" />
        <Button onClick={onClick}>{isSaving ? "Saving …" : cta}</Button>
        <span className="flex-1" />
        {viewer.avatarThumbUrl && (
          <button className="text-xs text-destructive" onClick={removeAvatar}>
            Remove
          </button>
        )}
      </div>
    </>
  )
}
